// The loan officer one-page application in English, Simplified Chinese and
// Spanish. Same mechanism as the borrower page (onePagerI18n.ts): each entry
// is [English, 简体中文, Español]. Keys the borrower page already has fall
// through to it, so field labels and validation messages read the same on
// both pages. The 1003 itself (section names, field labels) stays in English,
// as it does everywhere else in the LOS.

import {
  Lang as BorrowerLang,
  MsgKey as BorrowerMsgKey,
  translate as translateBorrower,
} from "../QuickIntake/GMCCQuickIntake/onePagerI18n";

export type Lang = BorrowerLang;
export { LANGS, LangContext, initialLang, rememberLang, DECLARATION_QUESTIONS } from "../QuickIntake/GMCCQuickIntake/onePagerI18n";

const S = {
  // Header and chrome
  tabTitleLO: ["Loan officer application", "贷款专员申请表", "Solicitud del oficial de préstamos"],
  signedInAs: ["Signed in as", "当前登录", "Sesión iniciada como"],
  loanLabel: ["Loan", "贷款", "Préstamo"],
  progressSaved: [
    "Progress is saved. Refresh or come back to this link anytime.",
    "进度已保存。随时可以刷新或通过此链接回来。",
    "El progreso está guardado. Actualice o vuelva a este enlace cuando quiera.",
  ],
  copyLink: ["Copy link", "复制链接", "Copiar enlace"],
  linkCopied: ["Link copied", "链接已复制", "Enlace copiado"],
  stepInfo: ["Application info", "申请信息", "Datos de la solicitud"],
  stepProcess: ["Process documents", "处理文件", "Procesar documentos"],
  stepReview: ["Review 1003", "审核 1003", "Revisar el 1003"],
  stepSync: ["Sync to Encompass", "同步到 Encompass", "Sincronizar con Encompass"],
  notAllowed: [
    "The loan officer application is not enabled for this company.",
    "贵公司尚未开通贷款专员申请表。",
    "La solicitud del oficial de préstamos no está habilitada para esta empresa.",
  ],
  loadFailed: ["Could not load this application.", "无法加载这份申请。", "No se pudo cargar esta solicitud."],
  notFound: ["This application was not found.", "找不到这份申请。", "No se encontró esta solicitud."],

  // Form
  newApplication: ["New application", "新申请", "Nueva solicitud"],
  formIntro: [
    "Enter what you have from the borrower. Only fields marked * are required. Documents you upload are used to prefill the rest of the 1003.",
    "填写您从借款人处已掌握的信息。仅带 * 的字段为必填。上传的文件将用于预填 1003 的其余部分。",
    "Ingrese lo que tenga del prestatario. Solo los campos marcados con * son obligatorios. Los documentos que suba se usan para prellenar el resto del 1003.",
  ],
  application: ["Application", "申请", "Solicitud"],
  sectionsComplete: ["{done} of {total} sections complete", "已完成 {done}/{total} 部分", "{done} de {total} secciones completas"],
  afterYouSync: ["After you sync", "同步之后", "Después de sincronizar"],
  sendCC: ["Send borrower a Consumer Connect link", "给借款人发送 Consumer Connect 链接", "Enviar al prestatario un enlace de Consumer Connect"],
  sendCCSub: [
    "They can review the application, finish missing items and upload documents.",
    "借款人可以查看申请、补齐缺失项并上传文件。",
    "Podrá revisar la solicitud, completar lo que falte y subir documentos.",
  ],
  secBorrower: ["Borrower", "借款人", "Prestatario"],
  borrowerSub: ["Primary borrower’s contact info", "主借款人的联系方式", "Datos de contacto del prestatario principal"],
  estimatedFico: ["Estimated FICO", "预估 FICO", "FICO estimado"],
  ficoHint: ["The borrower’s estimate is fine.", "借款人的估计即可。", "La estimación del prestatario es suficiente."],
  ssnHintLO: [
    "Encrypted. Only used for a credit pull with the borrower’s authorization.",
    "已加密。仅在获得借款人授权后用于查询信用。",
    "Cifrado. Solo se usa para una consulta de crédito con la autorización del prestatario.",
  ],
  coToggleTitleLO: ["Co-borrower", "共同借款人", "Coprestatario"],
  coToggleSubLO: ["Add a second borrower to this application", "为这份申请添加第二位借款人", "Agregar un segundo prestatario a esta solicitud"],
  coPartyNote: [
    "Their own 1003 party: income, assets and declarations are kept separately.",
    "作为独立的 1003 当事人：收入、资产和声明分别记录。",
    "Su propia parte en el 1003: ingresos, activos y declaraciones se registran por separado.",
  ],
  secLoanLO: ["Loan", "贷款", "Préstamo"],
  loanSubLO: ["Purpose and amount", "目的与金额", "Propósito y monto"],
  loanPurpose: ["Loan purpose", "贷款目的", "Propósito del préstamo"],
  purchaseTitle: ["Purchase", "购房", "Compra"],
  purchaseSub: ["New property purchase", "购买新房产", "Compra de una nueva propiedad"],
  refinanceSubLO: ["Rate/term or cash-out", "利率/期限或套现", "Tasa/plazo o retiro de efectivo"],
  firstTimeQ: ["First-time homebuyer?", "首次购房？", "¿Comprador por primera vez?"],
  secPropertyLO: ["Property", "房产", "Propiedad"],
  propertySubLO: ["No property yet? Enter the target city and state.", "还没有房产？填写目标城市和州。", "¿Aún sin propiedad? Ingrese la ciudad y el estado objetivo."],
  occupancy: ["Occupancy", "居住用途", "Ocupación"],
  secDeclLO: ["Declarations", "声明事项", "Declaraciones"],
  declSubLO: ["Form 1003, Section 5", "1003 表第 5 节", "Formulario 1003, Sección 5"],
  answeredOf: ["{answered} of {total} answered", "已回答 {answered}/{total}", "{answered} de {total} respondidas"],
  questionsBM: ["Questions B–M", "问题 B–M", "Preguntas B–M"],
  notAnsweredYet: ["{n} questions · not answered yet", "{n} 个问题 · 尚未回答", "{n} preguntas · sin responder"],
  groupSummary: ["{yes} Yes · {no} No · {left} left", "{yes} 是 · {no} 否 · 剩余 {left}", "{yes} Sí · {no} No · {left} pendientes"],
  allNo: ["All answered No", "全部回答“否”", "Todas respondidas No"],
  markAllNo: ["Mark all No", "全部标为“否”", "Marcar todas No"],
  reviewBtn: ["Review", "查看", "Revisar"],
  hide: ["Hide", "收起", "Ocultar"],
  markAllNote: [
    "“Mark all No” only answers B–M for {name}. Each borrower answers their own declarations.",
    "“全部标为否”只回答 {name} 的 B–M。每位借款人各自回答自己的声明。",
    "“Marcar todas No” solo responde B–M para {name}. Cada prestatario responde sus propias declaraciones.",
  ],
  markAllNoteSingle: [
    "“Mark all No” only answers B–M. Expand to review or change any answer before submitting.",
    "“全部标为否”只回答 B–M。提交前可展开查看或修改任一答案。",
    "“Marcar todas No” solo responde B–M. Expanda para revisar o cambiar cualquier respuesta antes de enviar.",
  ],
  declALO: [
    "Will the borrower occupy the property as their primary residence?",
    "借款人是否会将该房产作为主要居所？",
    "¿El prestatario ocupará la propiedad como residencia principal?",
  ],
  secDocsLO: ["Documents", "文件", "Documentos"],
  docsSubLO: ["Upload anything the borrower has already sent you.", "上传借款人已经发给您的任何文件。", "Suba lo que el prestatario ya le haya enviado."],
  chooseOrDrag: ["Choose files or drag here", "选择文件或拖到这里", "Elija archivos o arrástrelos aquí"],
  dropSubLO: ["PDF, JPG or PNG", "PDF、JPG 或 PNG", "PDF, JPG o PNG"],
  filesNote: [
    "Files are assigned to the person whose box they’re uploaded in. Put joint documents, like a shared bank statement, under the borrower.",
    "文件归属于所上传框对应的人。共同文件（如联名银行对账单）请放在借款人下。",
    "Los archivos se asignan a la persona en cuya casilla se suben. Los documentos conjuntos, como un estado de cuenta compartido, van con el prestatario.",
  ],
  nextNote: [
    "Next, we’ll process the uploaded documents and prefill the full 1003 for your review. Nothing is sent to Encompass until you confirm.",
    "接下来我们会处理上传的文件并预填完整的 1003 供您审核。在您确认之前不会向 Encompass 发送任何内容。",
    "A continuación procesaremos los documentos subidos y prellenaremos el 1003 completo para su revisión. No se envía nada a Encompass hasta que usted confirme.",
  ],
  uploadedTag: ["Uploaded", "已上传", "Subido"],
  savingApp: ["Saving…", "正在保存…", "Guardando…"],
  createFailed: ["Could not save the application. Please try again.", "申请保存失败，请重试。", "No se pudo guardar la solicitud. Inténtelo de nuevo."],
  uploadFailedLO: ["Some files could not be uploaded.", "部分文件上传失败。", "Algunos archivos no se pudieron subir."],

  // Processing
  processingTitle: ["Processing documents", "正在处理文件", "Procesando documentos"],
  processingLede: [
    "We’re reading the borrower’s documents and prefilling the 1003. The review will open automatically when it’s ready.",
    "正在读取借款人的文件并预填 1003。准备好后会自动打开审核页。",
    "Estamos leyendo los documentos del prestatario y prellenando el 1003. La revisión se abrirá automáticamente cuando esté lista.",
  ],
  docsUploaded: ["{n} documents uploaded", "已上传 {n} 份文件", "{n} documentos subidos"],
  personDocs: ["{name} ({role}) · {n} documents", "{name}（{role}）· {n} 份文件", "{name} ({role}) · {n} documentos"],
  noDocuments: ["No documents uploaded", "未上传文件", "No se subieron documentos"],
  leaveOpen: [
    "You can leave this page open. Nothing is sent to Encompass until you confirm on the next step.",
    "可以保持此页面打开。在下一步确认之前不会向 Encompass 发送任何内容。",
    "Puede dejar esta página abierta. No se envía nada a Encompass hasta que confirme en el siguiente paso.",
  ],
  pfTitle: ["We couldn’t process the documents", "文件处理失败", "No pudimos procesar los documentos"],
  pfLede: [
    "Processing didn’t finish. The application info you entered is saved on the loan.",
    "处理未能完成。您填写的申请信息已保存在贷款中。",
    "El procesamiento no terminó. La información que ingresó está guardada en el préstamo.",
  ],
  pfNote: [
    "Continuing without documents opens the 1003 review with only the fields entered on the form.",
    "不带文件继续将只用表单中填写的字段打开 1003 审核。",
    "Continuar sin documentos abre la revisión del 1003 solo con los campos ingresados en el formulario.",
  ],
  continueWithout: ["Continue without documents", "不带文件继续", "Continuar sin documentos"],
  tryAgain: ["Try again", "重试", "Intentar de nuevo"],

  // Review
  reviewTitle: ["Review 1003", "审核 1003", "Revisar el 1003"],
  reviewLede: ["{summary}. Everything below will be synced to Encompass.", "{summary}。以下全部内容将同步到 Encompass。", "{summary}. Todo lo siguiente se sincronizará con Encompass."],
  confirmSync: ["Confirm & sync to Encompass", "确认并同步到 Encompass", "Confirmar y sincronizar con Encompass"],
  urla: ["Uniform Residential Loan Application —", "统一住宅贷款申请表 —", "Solicitud Uniforme de Préstamo Residencial —"],
  roleBorrower: ["Borrower", "借款人", "Prestatario"],
  roleCoborrower: ["Co-borrower", "共同借款人", "Coprestatario"],
  combinedIncome: ["Combined monthly income", "合计月收入", "Ingreso mensual combinado"],
  monthlyIncomeOf: ["{name} monthly income", "{name} 月收入", "Ingreso mensual de {name}"],
  highlightNote: [
    "Highlighted fields were extracted from documents uploaded for this person. Sections marked shared apply to both.",
    "高亮字段来自为此人上传的文件。标注“共享”的部分适用于两位借款人。",
    "Los campos resaltados se extrajeron de los documentos subidos para esta persona. Las secciones marcadas como compartidas aplican a ambos.",
  ],
  fieldsFrom: ["{fields} fields from {docs} documents", "来自 {docs} 份文件的 {fields} 个字段", "{fields} campos de {docs} documentos"],
  fromDocs: ["{n} from documents", "{n} 项来自文件", "{n} de documentos"],
  edit: ["Edit", "编辑", "Editar"],
  shared: ["shared", "共享", "compartido"],
  sections1003: ["1003 sections", "1003 各节", "Secciones del 1003"],
  docsByPerson: ["Documents by person", "按人查看文件", "Documentos por persona"],
  docsProcessed: ["Documents processed", "已处理的文件", "Documentos procesados"],
  clickToPreview: ["Click a file or a source tag to preview it.", "点击文件或来源标签可预览。", "Haga clic en un archivo o en una etiqueta de origen para verlo."],
  nFields: ["{n} fields", "{n} 个字段", "{n} campos"],
  couldntRead: ["Couldn’t read", "无法读取", "No se pudo leer"],
  unreadableTitle: ["{n} documents couldn’t be read", "{n} 份文件无法读取", "{n} documentos no se pudieron leer"],
  unreadableTitleOne: ["1 document couldn’t be read", "1 份文件无法读取", "1 documento no se pudo leer"],
  unreadableNote: [
    "No fields were prefilled from these files. Enter those details yourself if needed.",
    "这些文件没有预填任何字段。如有需要请自行录入。",
    "No se prellenó ningún campo desde estos archivos. Ingrese esos datos usted mismo si es necesario.",
  ],
  dismiss: ["Dismiss", "关闭", "Descartar"],
  checkThenSync: [
    "Check the values above, then sync. You can still edit the loan in Encompass afterward.",
    "核对以上内容后再同步。之后仍可在 Encompass 中编辑该贷款。",
    "Revise los valores anteriores y luego sincronice. Después aún podrá editar el préstamo en Encompass.",
  ],
  checkBothThenSync: [
    "Check both borrowers, then sync. You can still edit the loan in Encompass afterward.",
    "核对两位借款人后再同步。之后仍可在 Encompass 中编辑该贷款。",
    "Revise a ambos prestatarios y luego sincronice. Después aún podrá editar el préstamo en Encompass.",
  ],
  nothingYet: ["Nothing entered yet", "尚未填写", "Aún sin datos"],
  edited: ["Edited", "已修改", "Editado"],
  extractedFrom: ["Extracted from this document", "从这份文件抽取的内容", "Extraído de este documento"],
  noFieldsFromDoc: ["No 1003 fields were filled from this document.", "这份文件没有填入任何 1003 字段。", "Ningún campo del 1003 se llenó desde este documento."],
  previewUnavailable: ["Preview is not available for this file.", "此文件无法预览。", "La vista previa no está disponible para este archivo."],
  loadingPreview: ["Loading document…", "正在加载文件…", "Cargando documento…"],
  edit1003Title: ["Edit 1003 · {section}", "编辑 1003 · {section}", "Editar 1003 · {section}"],
  doneEditing: ["Done", "完成", "Listo"],
  totalLabel: ["Total", "合计", "Total"],
  syncStartFailed: ["Could not start the sync. Please try again.", "无法开始同步，请重试。", "No se pudo iniciar la sincronización. Inténtelo de nuevo."],

  // Syncing / waiting / failed / synced
  syncingTitle: ["Syncing to Encompass", "正在同步到 Encompass", "Sincronizando con Encompass"],
  syncingLede: [
    "Creating the loan file and attaching documents. This page updates on its own when the sync finishes.",
    "正在创建贷款文件并附加文件。同步完成后此页面会自动更新。",
    "Creando el expediente del préstamo y adjuntando documentos. Esta página se actualiza sola cuando termine la sincronización.",
  ],
  syncingNote: [
    "You can close this page. The sync keeps running, and this link shows the latest status.",
    "可以关闭此页面。同步会继续进行，通过此链接可查看最新状态。",
    "Puede cerrar esta página. La sincronización sigue en marcha y este enlace muestra el estado más reciente.",
  ],
  lockedTitle: ["Waiting for the loan to be unlocked", "等待贷款解锁", "Esperando a que se desbloquee el préstamo"],
  lockedLede: [
    "The loan is locked in Encompass, usually because someone has it open. We’ll keep retrying automatically.",
    "该贷款在 Encompass 中被锁定，通常是有人正在打开它。我们会自动持续重试。",
    "El préstamo está bloqueado en Encompass, normalmente porque alguien lo tiene abierto. Seguiremos reintentando automáticamente.",
  ],
  waitingTitle: ["Encompass is not responding — retrying", "Encompass 暂时无响应，正在重试", "Encompass no responde; reintentando"],
  waitingLede: [
    "Encompass returned a temporary error. We’ll keep retrying automatically.",
    "Encompass 返回了临时错误。我们会自动持续重试。",
    "Encompass devolvió un error temporal. Seguiremos reintentando automáticamente.",
  ],
  lastAttempt: ["Last attempt", "上次尝试", "Último intento"],
  nextAttempt: ["Next attempt", "下次尝试", "Próximo intento"],
  attemptsSoFar: ["Attempts so far", "已尝试次数", "Intentos hasta ahora"],
  retryNote: [
    "Retries wait longer each time, up to 24 hours apart. Close the loan in Encompass, then use Retry now to sync right away.",
    "每次重试的间隔会越来越长，最长 24 小时。在 Encompass 中关闭该贷款后，点击“立即重试”可马上同步。",
    "Cada reintento espera más, hasta 24 horas. Cierre el préstamo en Encompass y use Reintentar ahora para sincronizar de inmediato.",
  ],
  lockedFoot: [
    "You can close this page. Nothing else is needed from you unless the lock stays.",
    "可以关闭此页面。除非锁定持续存在，否则无需您再做任何操作。",
    "Puede cerrar esta página. No necesita hacer nada más a menos que el bloqueo persista.",
  ],
  retryNow: ["Retry now", "立即重试", "Reintentar ahora"],
  failedTitle: ["Sync to Encompass failed", "同步到 Encompass 失败", "La sincronización con Encompass falló"],
  failedLede: [
    "Encompass returned an error. Your 1003 is saved here; fix the issue and retry.",
    "Encompass 返回了错误。您的 1003 已保存在此，请修复问题后重试。",
    "Encompass devolvió un error. Su 1003 está guardado aquí; corrija el problema y reintente.",
  ],
  errorFrom: ["Error from Encompass", "Encompass 返回的错误", "Error de Encompass"],
  failedAt: ["Failed at {time}", "失败于 {time}", "Falló a las {time}"],
  backToReview: ["Back to review", "返回审核", "Volver a la revisión"],
  retrySync: ["Retry sync", "重试同步", "Reintentar sincronización"],
  syncedTitle: ["Synced to Encompass", "已同步到 Encompass", "Sincronizado con Encompass"],
  syncedLede: [
    "The 1003 for {names} and their documents are now in Encompass.",
    "{names} 的 1003 及其文件现已在 Encompass 中。",
    "El 1003 de {names} y sus documentos ya están en Encompass.",
  ],
  encompassLoanId: ["Encompass loan ID", "Encompass 贷款 ID", "ID del préstamo en Encompass"],
  encompassLoanNumber: ["Encompass loan number", "Encompass 贷款编号", "Número de préstamo en Encompass"],
  linkInvalid: [
    "This link is not valid. Ask the loan officer for their current link.",
    "此链接无效，请向贷款官索取最新的链接。",
    "Este enlace no es válido. Pida al oficial de préstamos su enlace actual.",
  ],
  encompassFolder: ["Encompass folder", "Encompass 文件夹", "Carpeta en Encompass"],
  inEncompassNote: [
    "In Encompass, open the \"{folder}\" folder and find this loan {by}. Everything on the 1003 and all attached documents have been synced.",
    "在 Encompass 中打开“{folder}”文件夹，{by}即可找到这笔贷款。1003 的全部信息和所有文件都已同步过去。",
    "En Encompass, abra la carpeta \"{folder}\" y busque este préstamo {by}. Toda la información del 1003 y todos los documentos adjuntos se han sincronizado.",
  ],
  byLoanNumber: ["by its loan number", "按贷款编号", "por su número de préstamo"],
  byBorrowerName: ["by the borrower's name", "按借款人姓名", "por el nombre del prestatario"],
  testingInfoTitle: ["Testing info — Encompass sandbox login", "测试信息 — Encompass 沙箱登录", "Información de prueba — acceso al sandbox de Encompass"],
  testingInstance: ["Instance ID", "实例 ID", "ID de instancia"],
  testingUsername: ["Username", "用户名", "Usuario"],
  testingPassword: ["Password", "密码", "Contraseña"],
  testingNote: [
    "Sandbox only. Sign in to Encompass with these and find the loan by its number.",
    "仅沙箱。用这些登录 Encompass，按贷款编号找到这笔贷款。",
    "Solo sandbox. Inicie sesión en Encompass con estos datos y busque el préstamo por su número.",
  ],
  docsAttached: ["Documents attached", "已附加文件", "Documentos adjuntos"],
  syncedAt: ["Synced", "同步时间", "Sincronizado"],
  ccSentTo: ["Consumer Connect link sent to {email}", "Consumer Connect 链接已发送至 {email}", "Enlace de Consumer Connect enviado a {email}"],
  startAnother: ["Start another application", "开始另一份申请", "Iniciar otra solicitud"],
  and: [" and ", " 和 ", " y "],
} as const;

export type LOMsgKey = keyof typeof S | BorrowerMsgKey;
export type TranslateLO = (key: LOMsgKey, vars?: Record<string, string | number>) => string;

const COLUMN: Record<Lang, number> = { en: 0, zh: 1, es: 2 };

export const translateLO = (lang: Lang, key: LOMsgKey, vars?: Record<string, string | number>): string => {
  const entry = (S as Record<string, readonly string[]>)[key as string];
  if (!entry) return translateBorrower(lang, key as BorrowerMsgKey, vars);
  let text: string = entry[COLUMN[lang]] || entry[0];
  if (vars) {
    Object.keys(vars).forEach((k) => {
      text = text.split(`{${k}}`).join(String(vars[k]));
    });
  }
  return text;
};
